import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Autocomplete, Stack, TextField, Typography, Popper } from '@mui/material';
import PackagesApi from 'legacyfrontend/api/paquetes';
import { dataDefaultValues } from 'legacyfrontend/constants';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import ErrorSnackbar from '../ErrorSnackbar';
import DatePickerFullScreenDialog from './DatePickerFullScreenDialog';
import DateRangeSelector from './DateRangeSelector';
import OrigenDestinoFullScreenDialog from './OrigenDestinoFullScreenDialog';
import PasajerosFullScreenDialog from './PasajerosFullScreenDialog';
import PasajerosSelector from './PasajerosSelector';
import { addYearsToDate, getDisabledDates } from 'legacyfrontend/utils/dateUtils';

const FlightsFilterForm = ({
  handleOriginSelect,
  handleDestinationSelect,
  handleDateRangeSelect,
  handleOccupationChange,
  handleFlightPassenger,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  isDatesEmpty,
  isTuristasEmpty,
  setIsDatesEmpty,
  handleIsOnlyOneWay,
  onMobileSearchComplete,
  isMobileSearchActive = false,
}) => {
  const { t } = useTranslation('translations');
  const maxPassengers = 9;

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [totalTourists, setTotalTourists] = useState(1);
  const [adultsQuantity, setAdultsQuantity] = useState(1);
  const [childrenQuantity, setChildrenQuantity] = useState(0);
  const [babiesQuantity, setBabiesQuantity] = useState(0);
  const [passengerAges, setPassengerAges] = useState([30]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(formInput.origin ?? null);
  const [selectedDestination, setSelectedDestination] = useState(formInput.destination ?? null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isFullScreenRangeDatePickerOpen, setIsFullScreenRangeDatePickerOpen] = useState(false);
  const [shouldPasajerosOpen, setShouldPasajerosOpen] = useState(false);
  const [flightOrigins, setFlightOrigins] = useState(dataDefaultValues);
  const [flightDestinations, setFlightDestinations] = useState(dataDefaultValues);
  const [searchOrigin, setSearchOrigin] = useState('');
  const [searchDestination, setSearchDestination] = useState('');
  const [isOnlyOneWay, setIsOnlyOneWay] = useState(formInput?.isOnlyOneWay ?? false);

  const [isFirstSearch, setIsFirstSearch] = useState(true);

  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const isInitialMount = useRef(true);
  const isUpdatingFromFormInput = useRef(false);
  const prevStartDate = useRef(null);
  const prevEndDate = useRef(null);
  const pasajerosRef = useRef(null);

  const totalPassengers = adultsQuantity + childrenQuantity + babiesQuantity;

  const safeDate = (val) => {
    if (!val) return undefined;
    const d = val instanceof Date ? val : new Date(val);
    return isNaN(d) ? undefined : d;
  };

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleLlegoData = useCallback(
    (passengers, startDate, endDate) => {
      handleFlightPassenger(passengers);
    },
    [handleFlightPassenger]
  );

  const fetchAirports = async (value, isOrigin = true) => {
    try {
      const response = await PackagesApi.getAirports({ cityName: value });
      if (isOrigin) {
        setFlightOrigins({ isLoading: false, error: false, data: response });
      } else {
        setFlightDestinations({
          isLoading: false,
          error: false,
          data: response,
        });
      }
    } catch (error) {
      showError(error?.message);
      if (isOrigin) {
        setFlightOrigins({ data: [], error: true, isLoading: false });
      } else {
        setFlightDestinations({ data: [], error: true, isLoading: false });
      }
    }
  };

  const handleAirportChange = async (_, value, isOrigin) => {
    if (!value) {
      if (isOrigin) {
        setSearchOrigin('');
        setFlightOrigins({ isLoading: false, error: false, data: [] });
      } else {
        setSearchDestination('');
        setFlightDestinations({ isLoading: false, error: false, data: [] });
      }
      return;
    }

    if (isOrigin) {
      setSearchOrigin(value);
      setFlightOrigins({ isLoading: true, error: false, data: [] });
    } else {
      setSearchDestination(value);
      setFlightDestinations({ isLoading: true, error: false, data: [] });
    }

    try {
      await fetchAirports(value, isOrigin);
    } catch (error) {
    }
  };

  const debouncedAirportChange = React.useCallback(
    debounce((value, isOrigin) => {
      handleAirportChange(null, value, isOrigin);
    }, 300),
    []
  );

  const sortedFlightOrigins = (flightOrigins?.data || []).slice().sort((a, b) => {
    const aa = (a?.countryName || '').toString();
    const bb = (b?.countryName || '').toString();
    return aa.localeCompare(bb);
  });

  const sortedFlightDestinations = (flightDestinations?.data || []).slice().sort((a, b) => {
    const aa = (a?.countryName || '').toString();
    const bb = (b?.countryName || '').toString();
    return aa.localeCompare(bb);
  });

  const handleSearchChange = (value, isOrigin) => {
    if (isOrigin) {
      setSearchOrigin(value);
    } else {
      setSearchDestination(value);
    }
    debouncedAirportChange(value, isOrigin);
  };

  const handleOriginCountryCode = (value) => {
    const flightInfo = JSON.parse(sessionStorage.getItem('flightInfo') || '{}');
    sessionStorage.setItem(
      'flightInfo',
      JSON.stringify({
        ...flightInfo,
        originCountryCode: value.countryCode,
        originId: value.id,
        originName: value.cityName,
        originAirportCode: value.airportCode,
        originAirportName: value.airportName,
        originCityCode: value.cityCode,
      })
    );
  };

  const handleDestinationCountryCode = (value) => {
    const flightInfo = JSON.parse(sessionStorage.getItem('flightInfo') || '{}');
    sessionStorage.setItem(
      'flightInfo',
      JSON.stringify({
        ...flightInfo,
        destinationCountryCode: value.countryCode,
        destinationId: value.id,
        destinationName: value.cityName,
        destinationAirportCode: value.airportCode,
        destinationAirportName: value.airportName,
        destinationCityCode: value.cityCode,
      })
    );
  };

  const handleChangeOrigin = (value) => {
    handleOriginSelect(value);
    setSelectedOrigin(value);
    setIsOriginEmpty(false);
    if (originInputRef.current) originInputRef.current.blur();
    if (value) {
      handleOriginCountryCode(value);
    }
    if (destinationInputRef.current && value) {
      setTimeout(() => {
        destinationInputRef.current.focus();
      }, 200);
    }

    if (value) setIsDestinationOpen(true);
  };

  const handleChangeDestination = (value) => {
    handleDestinationSelect(value);
    setSelectedDestination(value);
    setIsDestinationEmpty(false);
    if (value) {
      setIsDestinationOpen(false);
      handleDestinationCountryCode(value);
      if (!startDate) {
        setFocusedInput('startDate');
        setIsFullScreenRangeDatePickerOpen(true);
      } else if (startDate && !endDate && !formInput.isOnlyOneWay) {
        setFocusedInput('endDate');
        setIsFullScreenRangeDatePickerOpen(true);
      }
    }
  };

  const handleSwitchChange = useCallback(
    (checked) => {
      setIsOnlyOneWay(checked);
      handleIsOnlyOneWay(checked);
      if (checked) {
        setEndDate(null);
      }
    },
    [handleIsOnlyOneWay]
  );

  const handlePassengerChange = useCallback(
    (type, value) => {
      const newQuantity = parseInt(value, 10) || 0;
      const otherQuantities =
        type === 'adults'
          ? childrenQuantity + babiesQuantity
          : type === 'children'
            ? adultsQuantity + babiesQuantity
            : adultsQuantity + childrenQuantity;

      if (newQuantity + otherQuantities <= maxPassengers) {
        switch (type) {
          case 'adults':
            setAdultsQuantity(newQuantity);
            break;
          case 'children':
            setChildrenQuantity(newQuantity);
            break;
          case 'babies':
            setBabiesQuantity(newQuantity);
            break;
        }
      } else {
        showError(`El número total de pasajeros no puede superar ${maxPassengers}.`);
      }
    },
    [adultsQuantity, childrenQuantity, babiesQuantity, showError]
  );

  const handlePasajerosClose = useCallback(() => {
    if (pasajerosRef.current) {
      pasajerosRef.current.setAttribute('inert', '');
    }
    setShouldPasajerosOpen(false);
    setIsFirstSearch(false);
    if (onMobileSearchComplete) {
      onMobileSearchComplete();
    }
  }, [onMobileSearchComplete]);

  const handlePasajerosOpen = useCallback(() => {
    if (selectedOrigin && selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.removeAttribute('inert');
      }
      setShouldPasajerosOpen(true);
    }
  }, [selectedOrigin, selectedDestination]);

  useEffect(() => {
    const {
      departureDate: newDeparture,
      returnDate: newReturn,
      isOnlyOneWay: newIsOnlyOneWay,
    } = formInput;

    isUpdatingFromFormInput.current = true;

    if (newDeparture) {
      setStartDate(newDeparture);
    }

    if (!newIsOnlyOneWay && newReturn) {
      setEndDate(newReturn);
    } else {
      setEndDate(null);
    }

    setTimeout(() => {
      isUpdatingFromFormInput.current = false;
      isInitialMount.current = false;
    }, 0);
  }, [formInput.departureDate, formInput.returnDate, formInput.isOnlyOneWay]);

  useEffect(() => {
    if (
      isInitialMount.current ||
      isUpdatingFromFormInput.current ||
      (startDate === prevStartDate.current && endDate === prevEndDate.current)
    ) {
      prevStartDate.current = startDate;
      prevEndDate.current = endDate;
      return;
    }

    handleDateRangeSelect(startDate, endDate);

    if (formInput.isOnlyOneWay && startDate) {
      handleDateRangeSelect(startDate, null);
      setFocusedInput(null);
    }
    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
  }, [startDate, endDate, formInput.isOnlyOneWay]);

  useEffect(() => {
    if (passengerAges.length > 0 && !isInitialMount.current && !isUpdatingFromFormInput.current) {
      const startOnly = startDate ? new Date(startDate).toISOString().split('T')[0] : null;
      const endOnly =
        !formInput.isOnlyOneWay && endDate ? new Date(endDate).toISOString().split('T')[0] : null;
      
      handleLlegoData(passengerAges, startOnly, endOnly);
    }
  }, [passengerAges, startDate, endDate, formInput.isOnlyOneWay]);

  useEffect(() => {
    if (startDate && endDate) setIsDatesEmpty(false);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = isClient ? window.innerWidth <= 768 : false;
      setIsMobile(prevIsMobile => {
        if (newIsMobile !== prevIsMobile) {
          return newIsMobile;
        }
        return prevIsMobile;
      });
    };
    handleResize();
    isClient && window.addEventListener('resize', handleResize);

    return () => isClient && window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedOrigin(formInput.origin ?? null);
  }, [formInput.origin]);

  useEffect(() => {
    setSelectedDestination(formInput.destination ?? null);
  }, [formInput.destination]);

  useEffect(() => {
    setTotalTourists(totalPassengers);
  }, [totalPassengers]);

  useEffect(() => {
    if (totalTourists > 0) {
      handleOccupationChange(totalTourists);
    }
  }, [totalTourists]);

  useEffect(() => {
    const adultsAges = Array(adultsQuantity).fill(30);
    const childrenAges = Array(childrenQuantity).fill(7);
    const babiesAges = Array(babiesQuantity).fill(1);

    setPassengerAges([...adultsAges, ...childrenAges, ...babiesAges]);
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  useEffect(() => {
    const urlParams = new URLSearchParams(isClient ? window.location.search : '');
    const passengersParam = urlParams.get('passengers');

    if (passengersParam) {
      try {
        const decodedPassengers = decodeURIComponent(passengersParam);
        const passengersArray = decodedPassengers.split(',').map(Number);

        if (passengersArray.length > 0) {
          const adults = passengersArray.filter((age) => age === 30).length;
          const children = passengersArray.filter((age) => age === 7).length;
          const babies = passengersArray.filter((age) => age === 1).length;
          
          setAdultsQuantity(adults || 1);
          setChildrenQuantity(children || 0);
          setBabiesQuantity(babies || 0);
        }
      } catch (error) {
        setAdultsQuantity(1);
      }
    }
  }, []);

  useEffect(() => {
    if (adultsQuantity === 0 && childrenQuantity === 0 && babiesQuantity === 0) {
      setAdultsQuantity(1);
    }
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  useEffect(() => {
    if (!selectedOrigin || !selectedDestination) {
      if (pasajerosRef.current) {
        pasajerosRef.current.setAttribute('inert', '');
      }
      setShouldPasajerosOpen(false);
    } else if (pasajerosRef.current) {
      pasajerosRef.current.removeAttribute('inert');
    }
  }, [selectedOrigin, selectedDestination]);

  const ListboxProps = useMemo(
    () => ({
      style: {
        maxHeight: '400px',
        width: 'max-content',
        minWidth: '200px',
        maxWidth: '350px',
      },
    }),
    []
  );

  const PopperComponent = useMemo(
    () => (props) => (
      <Popper
        {...props}
        style={{
          ...props.style,
          width: 'max-content',
          minWidth: '200px',
          maxWidth: '350px',
        }}
        placement='bottom-start'
      />
    ),
    []
  );

  const renderGroup = useMemo(
    () => (params) => (
      <li key={`${params.group}-${params.children?.length || 0}`}>
        <div
          style={{
            padding: '8px 16px',
            fontWeight: 500,
            color: '#666',
          }}
        >
          {params.group}
        </div>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    []
  );

  return (
    <>
      <Stack className='input-container'>
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Desde dónde?</h3>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            handleAirportChange={(_, value) => handleSearchChange(value, true)}
            isFlight
            isLoading={flightOrigins?.isLoading}
            isOriginEmpty={isOriginEmpty}
            onSelect={handleOriginSelect}
            options={flightOrigins?.data}
            originSelected={selectedOrigin}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsOriginEmpty={setIsOriginEmpty}
            autoOpen={!isMobileSearchActive}
          />
        ) : (
          <Autocomplete
            ListboxProps={ListboxProps}
            PopperComponent={PopperComponent}
            className={`form__field origin-typeahead ${isOriginEmpty ? 'autocomplete-error' : ''}`}
            clearOnBlur
            defaultValue={formInput.origin}
            getOptionLabel={(option) =>
              `${option.cityName} - ${option.airportName || option.cityName} (${option.airportCode || option.cityCode})`
            }
            groupBy={(option) => option.countryName || 'Otros'}
            id='origin-autocomplete'
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={flightOrigins?.isLoading}
            loadingText='Cargando orígenes'
            noOptionsText='No hay resultados'
            onChange={(_, v) => handleChangeOrigin(v)}
            onFocus={() => setIsOriginEmpty(false)}
            onInputChange={(_, value) => handleSearchChange(value, true)}
            options={sortedFlightOrigins}
            renderGroup={renderGroup}
            renderInput={(params) => (
              <TextField
                {...params}
                className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
                inputRef={(input) => (originInputRef.current = input)}
                onFocus={() => {
                  const originInput = document.querySelector('.origin-typeahead');
                  if (originInput) {
                    const closeBtn = originInput.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleOriginSelect(undefined);
                }}
              />
            )}
            size='small'
            style={{ width: '100%' }}
            value={selectedOrigin}
          />
        )}
        <Stack
          flexDirection='row'
          alignSelf='flex-end'
          alignItems='center'
          zIndex={isOriginEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            Ingresa tu origen
          </Typography>
        </Stack>
      </Stack>

      <Stack className='input-container'>
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Hasta dónde?</h3>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            disabled={false}
            handleAirportChange={(_, value) => handleSearchChange(value, false)}
            isDestination
            isDestinationEmpty={isDestinationEmpty}
            isFlight
            isLoading={flightDestinations?.isLoading}
            isOpen={isDestinationDialogOpen}
            onSelect={handleChangeDestination}
            options={flightDestinations?.data}
            originSelected={selectedDestination}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsDestinationEmpty={setIsDestinationEmpty}
            setIsFullScreenRangeDatePickerOpen={setIsFullScreenRangeDatePickerOpen}
            autoOpen={!isMobileSearchActive}
          />
        ) : (
          <Autocomplete
            ListboxProps={ListboxProps}
            PopperComponent={PopperComponent}
            className={`form__field destination-typeahead ${isDestinationEmpty ? 'autocomplete-error' : ''}`}
            clearOnBlur
            defaultValue={formInput.destination}
            getOptionLabel={(option) =>
              `${option.cityName} - ${option.airportName || option.cityName} (${option.airportCode || option.cityCode})`
            }
            groupBy={(option) => option.countryName || 'Otros'}
            id='destination-autocomplete'
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={flightDestinations?.isLoading}
            loadingText='Cargando destinos'
            noOptionsText='No hay resultados'
            onChange={(e, v) => handleChangeDestination(v)}
            onFocus={() => setIsDestinationEmpty(false)}
            onInputChange={(_, value) => handleSearchChange(value, false)}
            options={sortedFlightDestinations}
            renderGroup={renderGroup}
            renderInput={(params) => (
              <TextField
                {...params}
                className='focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent'
                inputRef={(input) => (destinationInputRef.current = input)}
                onFocus={() => {
                  const input = document.querySelector('.destination-typeahead');
                  if (input) {
                    const closeBtn = input.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleDestinationSelect(undefined);
                }}
              />
            )}
            size='small'
            style={{ width: '100%' }}
            value={selectedDestination}
          />
        )}
        <Stack
          alignItems='center'
          alignSelf='flex-end'
          className='warning-animated-message'
          flexDirection='row'
          zIndex={isDestinationEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            Ingresa tu destino
          </Typography>
        </Stack>
      </Stack>

      <Stack className={`form__field date-picker ${isDatesEmpty ? 'dates-error' : ''}`}>
        {!isMobile && (
          <Stack flexDirection='row' justifyContent='space-between'>
            <Stack alignItems='center' flexDirection='row' gap={1}>
              <Typography color='#4f4f4f' fontSize={12}>
                Solo ida
              </Typography>
              <Switch checked={isOnlyOneWay} height={16} onChange={handleSwitchChange} width={32} />
            </Stack>
          </Stack>
        )}
        {isMobile ? (
          <DatePickerFullScreenDialog
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            handleSwitchChange={handleSwitchChange}
            isDatesEmpty={isDatesEmpty}
            isOnlyOneWay={isOnlyOneWay}
            isOpen={isFullScreenRangeDatePickerOpen}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
              if (!isOnlyOneWay && newEnd && newEnd !== newStart) {
                setIsFullScreenRangeDatePickerOpen(false);
              }
            }}
            setShouldPasajerosOpen={setShouldPasajerosOpen}
            startDate={safeDate(startDate)}
          />
        ) : (
          <DateRangeSelector
            startDate={safeDate(startDate)}
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
            }}
            error={isDatesEmpty}
            helperText={
              isDatesEmpty
                ? isOnlyOneWay
                  ? 'Selecciona la fecha de ida'
                  : 'Selecciona las fechas'
                : ''
            }
            disabled={false}
            disabledDates={getDisabledDates()}
            isOneWay={isOnlyOneWay}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            minDate={new Date()}
          />
        )}
        <Stack
          alignItems='center'
          alignSelf='flex-end'
          flexDirection='row'
          zIndex={isDatesEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography color='rgb(207, 24, 0)' fontSize={11}>
            Selecciona fechas
          </Typography>
        </Stack>
      </Stack>

      <Stack
        className={`pasajeros-input-container ${!selectedOrigin || !selectedDestination ? 'disabled' : ''
          }`}
        ref={pasajerosRef}
        sx={{
          opacity: !selectedOrigin || !selectedDestination ? 0.5 : 1,
          pointerEvents: !selectedOrigin || !selectedDestination ? 'none' : 'auto',
          cursor: !selectedOrigin || !selectedDestination ? 'not-allowed' : 'pointer',
        }}
      >
        <Typography color='#4f4f4f' fontSize={12}>
          ¿Cuántos viajan?
        </Typography>
        <Stack>
          {isMobile ? (
            <PasajerosFullScreenDialog
              adultsQuantity={adultsQuantity}
              babiesQuantity={babiesQuantity}
              childrenQuantity={childrenQuantity}
              disabled={!selectedOrigin || !selectedDestination}
              errorMessage={errorMessage}
              onClose={handlePasajerosClose}
              onOpen={handlePasajerosOpen}
              setAdultsQuantity={(value) => handlePassengerChange('adults', value)}
              setBabiesQuantity={(value) => handlePassengerChange('babies', value)}
              setChildrenQuantity={(value) => handlePassengerChange('children', value)}
              setTotalTourists={setTotalTourists}
              shouldOpen={shouldPasajerosOpen && selectedOrigin && selectedDestination}
              totalTourists={totalTourists}
            />
          ) : (
            <PasajerosSelector
              adultsQuantity={adultsQuantity}
              babiesQuantity={babiesQuantity}
              childrenQuantity={childrenQuantity}
              disabled={!selectedOrigin || !selectedDestination}
              isTuristasEmpty={isTuristasEmpty}
              onClick={handlePasajerosOpen}
              onClose={handlePasajerosClose}
              setAdultsQuantity={(value) => handlePassengerChange('adults', value)}
              setBabiesQuantity={(value) => handlePassengerChange('babies', value)}
              setChildrenQuantity={(value) => handlePassengerChange('children', value)}
              totalTourists={totalTourists}
            />
          )}
        </Stack>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </Stack>
    </>
  );
};

export default FlightsFilterForm;
